import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  DndContext,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDraggable,
  useDroppable,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { RotateCcw, HelpCircle, Coins } from "lucide-react";
import ElementaryMissionPage from "./ElementaryMissionPage";
import LemonBotJr from "@/components/LemonBotJr";
import MissionCompleteOverlay from "@/components/MissionCompleteOverlay";
import { loadJrState, saveJrState, markMissionComplete } from "@/utils/jrDemoState";
import {
  playPickup,
  playCoin,
  playError,
  playCorrect,
  playCelebrate,
  playClick,
} from "@/utils/jrSound";
import { speak } from "@/utils/jrVoice";

const COINS = [
  { key: "quarter", label: "25¢", value: 25, color: "bg-amber-200", ring: "ring-amber-500" },
  { key: "dime",    label: "10¢", value: 10, color: "bg-slate-200", ring: "ring-slate-500" },
  { key: "nickel",  label: "5¢",  value: 5,  color: "bg-zinc-200",  ring: "ring-zinc-500" },
  { key: "penny",   label: "1¢",  value: 1,  color: "bg-orange-200",ring: "ring-orange-500" },
];

/** Generate a target price that can be exactly made with the 4 coin types. */
function randomPrice() {
  // Range 16¢–99¢ keeps it interesting but not overwhelming.
  return 16 + Math.floor(Math.random() * 84);
}

function Coin({ coin, dragId }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: dragId,
    data: coin,
  });
  const style = {
    transform: CSS.Translate.toString(transform),
    opacity: isDragging ? 0.6 : 1,
  };
  return (
    <button
      ref={setNodeRef}
      {...attributes}
      {...listeners}
      type="button"
      style={style}
      className={`${coin.color} ring-4 ${coin.ring} rounded-full w-16 h-16 sm:w-20 sm:h-20 flex items-center justify-center font-black text-gray-900 shadow-md hover:shadow-lg active:scale-95 transition-transform cursor-grab active:cursor-grabbing touch-none select-none`}
      data-testid={`coin-${coin.key}`}
      aria-label={`Drag a ${coin.label} coin`}
    >
      <span className="text-base sm:text-lg">{coin.label}</span>
    </button>
  );
}

function Register({ total, target, over }) {
  const { setNodeRef, isOver } = useDroppable({ id: "register" });
  const done = total === target;
  const tooMuch = total > target;
  return (
    <div
      ref={setNodeRef}
      className={`rounded-3xl p-6 sm:p-8 ring-4 transition-all ${
        isOver || over
          ? "ring-yellow-500 bg-yellow-50 scale-[1.01]"
          : done
          ? "ring-green-500 bg-green-50"
          : tooMuch
          ? "ring-rose-400 bg-rose-50"
          : "ring-yellow-300 bg-white"
      }`}
      data-testid="coin-register"
      aria-label="Coin register drop zone"
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Coins className="w-6 h-6 text-yellow-600" />
          <span className="text-xs font-bold text-yellow-800 uppercase tracking-wide">Register</span>
        </div>
        <div className="text-xs font-bold text-gray-500">Drop coins here</div>
      </div>
      <div className="mt-4 flex items-baseline gap-3">
        <p className={`text-5xl sm:text-6xl font-black tabular-nums ${done ? "text-green-600" : tooMuch ? "text-rose-600" : "text-gray-900"}`} data-testid="coin-total">
          {total}¢
        </p>
        <p className="text-base text-gray-500">of {target}¢</p>
      </div>
      <div className="mt-3 h-2 w-full rounded-full bg-yellow-100 overflow-hidden" aria-hidden="true">
        <div
          className={`h-full transition-all duration-300 ${
            done ? "bg-green-500" : tooMuch ? "bg-rose-500" : "bg-yellow-500"
          }`}
          style={{ width: `${Math.min(100, (total / target) * 100)}%` }}
        />
      </div>
      <p className={`mt-3 text-sm font-bold ${done ? "text-green-700" : tooMuch ? "text-rose-700" : "text-gray-600"}`} data-testid="coin-status">
        {done
          ? "Perfect! You got it exactly right!"
          : tooMuch
          ? `Too many! You're ${total - target}¢ over.`
          : `Keep going — ${target - total}¢ more to go.`}
      </p>
    </div>
  );
}

export default function Mission6() {
  const navigate = useNavigate();
  const saved = loadJrState();
  const [price, setPrice] = useState(randomPrice());
  const [total, setTotal] = useState(0);
  const [round, setRound] = useState(1);
  const [roundsCompleted, setRoundsCompleted] = useState(0);
  const [usedHelper, setUsedHelper] = useState(false);
  const [showOverlay, setShowOverlay] = useState(false);
  const [dragCount, setDragCount] = useState(0); // used to reset coin instance ids

  // Sensors configured for both mouse and touch (iPad-friendly).
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 80, tolerance: 5 } })
  );

  const TOTAL_ROUNDS = 3;
  const roundDone = total === price;

  const handleDragStart = () => {
    playPickup();
  };

  const handleDragEnd = (event) => {
    const { over, active } = event;
    if (!over || over.id !== "register") return;
    const coin = active.data?.current;
    if (!coin) return;
    const next = total + coin.value;
    if (next > price) {
      playError();
      speak(`Oops! That's too much. Try a smaller coin.`);
      return;
    }
    setTotal(next);
    setDragCount((c) => c + 1);
    if (next === price) {
      playCorrect();
      speak("Perfect! You got it exactly right!");
    } else {
      playCoin();
    }
  };

  const handleNextRound = () => {
    playClick();
    const newRoundsCompleted = roundsCompleted + 1;
    setRoundsCompleted(newRoundsCompleted);
    if (newRoundsCompleted >= TOTAL_ROUNDS) {
      playCelebrate();
      const customized = !usedHelper && newRoundsCompleted === TOTAL_ROUNDS;
      saveJrState({
        coinGameBestRounds: Math.max(saved.coinGameBestRounds || 0, newRoundsCompleted),
        coinGameUsedHelper: usedHelper,
      });
      markMissionComplete(6, { customized });
      setShowOverlay(true);
      speak("Amazing job! You counted every coin correctly. You're a money math superstar!");
      return;
    }
    setPrice(randomPrice());
    setTotal(0);
    setRound(round + 1);
  };

  const handleReset = () => {
    playClick();
    setTotal(0);
  };

  const handleHelper = () => {
    playClick();
    setUsedHelper(true);
    // Auto-fill with the greedy-coin algorithm.
    let remaining = price - total;
    let next = total;
    const sorted = [...COINS].sort((a, b) => b.value - a.value);
    for (const c of sorted) {
      while (remaining >= c.value) {
        remaining -= c.value;
        next += c.value;
      }
    }
    setTotal(next);
    playCorrect();
    speak(`Here's the answer: ${price} cents exactly!`);
  };

  // Reset-to-zero coin instances so each drop creates a fresh draggable id.
  const coinInstances = useMemo(
    () =>
      COINS.map((c, i) => ({
        ...c,
        dragId: `${c.key}-${i}-${dragCount}`,
      })),
    [dragCount]
  );

  return (
    <ElementaryMissionPage
      missionNumber={6}
      title="Money Mission"
      lesson="Junior CEOs count coins! Help the customer pay the right amount."
      canContinue={roundDone}
      onContinue={handleNextRound}
      continueLabel={
        roundsCompleted + 1 >= TOTAL_ROUNDS ? "Finish Mission 6" : `Next Round (${round + 1}/${TOTAL_ROUNDS})`
      }
      testId="elementary-mission-6-page"
    >
      <div className="mb-5">
        <LemonBotJr
          tone="green"
          greeting={`A customer wants a lemonade for ${price} cents. Drag the right coins onto the register!`}
          prompts={[
            "Tap a coin and drag it onto the register.",
            "Try big coins first — quarters, then dimes.",
            "You can't go over! Be exact.",
          ]}
          compact
        />
      </div>

      <div className="mb-3 flex items-center justify-between">
        <div className="inline-flex items-center gap-2 bg-yellow-100 ring-1 ring-yellow-300 rounded-full px-3 py-1 text-xs font-bold text-yellow-800">
          <Coins className="w-3.5 h-3.5" />
          Round {round} of {TOTAL_ROUNDS}
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleHelper}
            className="text-xs font-bold text-sky-700 hover:text-sky-900 bg-sky-50 hover:bg-sky-100 ring-1 ring-sky-200 rounded-full px-3 py-1.5 inline-flex items-center gap-1"
            data-testid="mission-6-helper"
          >
            <HelpCircle className="w-3.5 h-3.5" />
            Show me
          </button>
          <button
            type="button"
            onClick={handleReset}
            className="text-xs font-bold text-gray-700 hover:text-gray-900 bg-white hover:bg-gray-50 ring-1 ring-gray-200 rounded-full px-3 py-1.5 inline-flex items-center gap-1"
            data-testid="mission-6-reset"
          >
            <RotateCcw className="w-3.5 h-3.5" />
            Clear
          </button>
        </div>
      </div>

      <DndContext sensors={sensors} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
        <Register total={total} target={price} />

        <div className="mt-6" data-testid="mission-6-coin-tray">
          <p className="text-xs font-bold text-gray-600 uppercase tracking-wide mb-3">
            Coin Tray — drag a coin
          </p>
          <div className="flex flex-wrap gap-3 sm:gap-4 justify-center bg-gradient-to-br from-amber-50 to-yellow-50 ring-1 ring-yellow-200 rounded-3xl p-5 sm:p-6">
            {coinInstances.map((c) => (
              <Coin key={c.dragId} dragId={c.dragId} coin={c} />
            ))}
          </div>
        </div>
      </DndContext>

      {showOverlay && (
        <MissionCompleteOverlay
          missionNumber={6}
          missionTitle="Money Mission"
          message={
            usedHelper
              ? "Nice work! You got every round right. Money math takes practice!"
              : "Amazing! 3 perfect rounds with no help. You're a money math superstar!"
          }
          continueLabel="Back to CEO City"
          onContinue={() => navigate("/elementary/demo")}
        />
      )}
    </ElementaryMissionPage>
  );
}
